/* eslint-disable @typescript-eslint/no-non-null-assertion */
/**
 * Adapted from https://github.com/lukewarlow/dialog-closedby, which is not published on npm.
 * Ported to TypeScript and cleaned up.
 *
 * Needed because `closedBy` is not yet widely supported outside of Chrome.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/closedBy
 */

type ClosedBy = 'none' | 'any' | 'closerequest'
declare global {
  interface HTMLDialogElement extends HTMLElement {
    closedBy: ClosedBy
  }
}

/**
 * @returns true if the browser supports `HTMLDialogElement.closedBy`
 */
export function isSupported() {
  return (
    typeof HTMLDialogElement !== 'undefined' &&
    typeof HTMLDialogElement.prototype === 'object' &&
    'closedBy' in HTMLDialogElement.prototype
  )
}

/** Tracks dialogs (by document) with `closedBy="any"` */
const dialogList = new WeakMap<Document, Set<HTMLDialogElement>>()
/** Tracks all dialogs (by document) */
const anyDialogList = new WeakMap<Document, Set<HTMLDialogElement>>()

/** Event listener for `pointerdown` events, to allow closing dialogs by clicking outside */
function lightDismissOpenDialogs(event: PointerEvent) {
  if (!event.isTrusted) return
  // Composed path allows us to find the target within shadowroots
  const target = event.composedPath()[0] as HTMLElement
  if (!target) return

  const document = target.ownerDocument
  if (anyDialogList.has(document)) {
    const dialogs = Array.from(anyDialogList.get(document)!).reverse()
    for (const dialog of dialogs) {
      const rect = dialog.getBoundingClientRect()
      const isInDialog =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width
      if (isInDialog) {
        break
      }
      dialog.close()
    }
  }
}

/**
 * Event listener for `keydown` events, to prevent closing dialogs by pressing
 * the Escape key, when `closedBy="none"`
 */
function preventEscapeCloseForNoneDialogs(event: KeyboardEvent) {
  if (!event.isTrusted || event.key !== 'Escape') return

  // Composed path allows us to find the target within shadowroots
  const target = event.composedPath()[0] as HTMLElement
  if (!target) return

  const document = target.ownerDocument
  if (dialogList.has(document)) {
    const dialogs = Array.from(dialogList.get(document)!)
    const topDialog = dialogs.at(-1)!
    if (topDialog.closedBy === 'none' && topDialog.matches(':modal')) {
      event.preventDefault()
    }
  }
}

/** This enables us to catch dialogs rendered in shadow DOM subtrees */
function observeShadowRoots<E extends Element>(
  ElementClass?: { new (): E },
  callback?: (node: Node) => void,
) {
  if (!ElementClass || !callback) return
  const originalAddShadow = ElementClass.prototype.attachShadow
  ElementClass.prototype.attachShadow = function (init: ShadowRootInit) {
    const shadow = originalAddShadow.call(this, init)
    callback(shadow)
    return shadow
  }
}

/**
 * Applies a polyfill for the `closedBy` property on `HTMLDialogElement` elements
 */
export function apply() {
  if (typeof window === 'undefined' || isSupported()) return

  const originalShow = HTMLDialogElement.prototype.show
  const originalShowModal = HTMLDialogElement.prototype.showModal
  const originalClose = HTMLDialogElement.prototype.close

  // Patch `HTMLDialogElement`'s show/showModal/close methods
  Object.assign(HTMLDialogElement.prototype, {
    show(this: HTMLDialogElement) {
      originalShow.call(this)
      const document = this.ownerDocument
      if (!dialogList.has(document)) {
        dialogList.set(document, new Set())
      }
      dialogList.get(document)?.add(this)
    },
    showModal(this: HTMLDialogElement) {
      originalShowModal.call(this)
      if (!dialogList.has(this.ownerDocument)) {
        dialogList.set(this.ownerDocument, new Set([this]))
      } else {
        dialogList.get(this.ownerDocument)?.add(this)
      }
      if (this.closedBy === 'any') {
        if (!anyDialogList.has(this.ownerDocument)) {
          anyDialogList.set(this.ownerDocument, new Set([this]))
        } else {
          anyDialogList.get(this.ownerDocument)?.add(this)
        }
      }
    },
    close(this: HTMLDialogElement, returnValue?: string) {
      originalClose.call(this, returnValue)
      const document = this.ownerDocument
      if (anyDialogList.has(document)) {
        anyDialogList.get(document)?.delete(this)
      }
      if (dialogList.has(document)) {
        dialogList.get(document)?.delete(this)
      }
    },
  })

  // Listen for changes to `closedby` attributes on dialog elements
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const target = mutation.target as HTMLElement
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'closedby' &&
        target.tagName === 'DIALOG'
      ) {
        // TODO: Check what the expected behaviour here is. Closing is just the easiest.
        if (mutation.oldValue !== target.getAttribute('closedby')) {
          const dialog = target as HTMLDialogElement
          dialog.close()
        }
      }
    }
  })

  const observerOptions: MutationObserverInit = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['closedby'],
  }
  observer.observe(document, observerOptions)
  observeShadowRoots(globalThis.HTMLElement, (shadow) => {
    observer.observe(shadow, observerOptions)
  })

  document.addEventListener('pointerdown', lightDismissOpenDialogs)
  document.addEventListener('keydown', preventEscapeCloseForNoneDialogs)
}
